import {
  GlobalSearchAnalyticsSession,
  outcomeForEntity,
  type SearchResultMetadata,
} from 'analytics/globalSearchAnalytics';
import type {SearchAnalyticsResult, SearchRankingVariant} from 'analytics/searchAnalytics';
import {aiDrawerViewAtom, aiSearchPendingQuestionAtom} from 'atoms/aiSearch';
import {entryPointForSearch} from 'components/search/useGlobalSearchAnalytics';
import {PEOPLE_SEARCH_RANKING_MODE} from 'hooks/useEmployee';
import {useStore} from 'jotai';
import {useCallback, useEffect, useRef} from 'react';

import type {SearchFilter} from './SearchFilters';
import {SearchRow} from './searchRows';

const RANKING = PEOPLE_SEARCH_RANKING_MODE as SearchRankingVariant;

const metadataOf = (item: object) =>
  (item as {search_metadata?: SearchResultMetadata}).search_metadata;

/** A row as today's box's analytics name it; null for meetings and asks. */
const analyticsResult = (row: SearchRow, rank: number): SearchAnalyticsResult | null => {
  switch (row.kind) {
    case 'person':
      return {
        entityType: 'person',
        localIdentity: `people:${row.person.ldap}`,
        rank,
        metadata: metadataOf(row.person),
      };
    case 'team':
      return {
        entityType: 'team',
        localIdentity: `teams:${row.team.slug}`,
        rank,
        metadata: metadataOf(row.team),
      };
    case 'workstream':
      return {
        entityType: 'workstream',
        localIdentity: `workstream:${row.workstream.id}`,
        rank,
        metadata: metadataOf(row.workstream),
      };
    case 'news':
      return {
        entityType: 'news',
        localIdentity: `news:${row.post.id}`,
        rank,
        metadata: metadataOf(row.post),
      };
    case 'knowledge':
      return {
        entityType: 'knowledge',
        localIdentity: `knowledge:${row.result.id}`,
        rank,
        metadata: metadataOf(row.result),
      };
    default:
      return null;
  }
};

/**
 * The search box's GLOBAL_SEARCH_* session, the events today's box sends, each
 * with `search_box: 'relaunch'`: it starts when the box opens, logs a results
 * view (and impressions) each time a query's results land, and ends on a pick,
 * on an Ask opened from anywhere in the box (a question, a chip, a pane's ask),
 * or when the box closes (as `pane_viewed` if something was looked at in the
 * pane, since the pane answers a lookup without leaving the box). A box that stays open after it ends (the search app)
 * starts the next one with the next query.
 */
export const useBoxSearchAnalytics = ({
  enabled,
  entryPoint,
  live,
  rows,
  loaded,
  filter,
}: {
  enabled: boolean;
  /** Where search was opened from (SEARCH_SHOWN's `source`). */
  entryPoint: string | false;
  live: string;
  /** The rows on screen for `live`, the Ask row included. */
  rows: SearchRow[];
  loaded: boolean;
  filter: SearchFilter;
}) => {
  const store = useStore();
  const sessionRef = useRef<GlobalSearchAnalyticsSession>();
  const requestRef = useRef<{id: string; filter: SearchFilter; live: string}>();
  const filterRef = useRef(filter);
  filterRef.current = filter;
  const previousFilterRef = useRef<SearchFilter>('all');
  const startedAtRef = useRef(performance.now());
  const lastSignatureRef = useRef<string>();
  const cleanupTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const paneViewedRef = useRef(false);

  const session = useCallback(() => {
    if (!sessionRef.current || sessionRef.current.isEnded) {
      sessionRef.current = new GlobalSearchAnalyticsSession(
        entryPointForSearch(entryPoint),
        RANKING,
        'relaunch'
      );
      requestRef.current = undefined;
      lastSignatureRef.current = undefined;
      paneViewedRef.current = false;
    }
    return sessionRef.current;
  }, [entryPoint]);

  useEffect(() => {
    if (!enabled) return undefined;
    clearTimeout(cleanupTimerRef.current);
    session();
    const asked = () =>
      sessionRef.current?.end({
        activeFilter: filterRef.current,
        endReason: 'selected',
        outcome: 'ask_opened',
      });
    const unsubscribe = [
      store.sub(aiSearchPendingQuestionAtom, () => {
        if (store.get(aiSearchPendingQuestionAtom)) asked();
      }),
      store.sub(aiDrawerViewAtom, () => {
        if (store.get(aiDrawerViewAtom) === 'expanded') asked();
      }),
    ];
    return () => {
      unsubscribe.forEach((stop) => stop());
      // StrictMode replays effects at once; only a real close ends the session.
      cleanupTimerRef.current = setTimeout(() => {
        sessionRef.current?.end({
          activeFilter: filterRef.current,
          outcome: paneViewedRef.current ? 'pane_viewed' : 'not_applicable',
        });
        sessionRef.current = undefined;
      }, 0);
    };
    // One session per open; the entry point is the one it opened with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    startedAtRef.current = performance.now();
  }, [live, filter]);

  useEffect(() => {
    if (!enabled) return;
    if (!live) {
      requestRef.current = undefined;
      return;
    }
    if (!loaded) return;
    // The query only dedupes here; it's never sent.
    const signature = `${live}\u0000${filter}`;
    if (lastSignatureRef.current === signature) return;
    lastSignatureRef.current = signature;

    const found = rows.flatMap((row, rank) => {
      const result = analyticsResult(row, rank);
      return result ? [result] : [];
    });
    const current = session();
    const {requestId} = current.resultsViewed({
      query: live,
      activeFilter: filter,
      previousFilter: previousFilterRef.current,
      resultCount: found.length,
      shownTopK: found.length,
      elapsedMs: performance.now() - startedAtRef.current,
      rankingVariant: RANKING,
      askRowPromoted: rows[0]?.kind === 'ask',
    });
    previousFilterRef.current = filter;
    requestRef.current = {id: requestId, filter, live};
    current.resultsImpressed({requestId, activeFilter: filter, results: found});
  }, [enabled, live, loaded, filter, rows, session]);

  /** A row was opened (`index` is its place in the list): log it and end the session. */
  const selected = useCallback(
    (row: SearchRow, index: number | undefined, method: 'click' | 'keyboard') => {
      const current = sessionRef.current;
      if (!enabled || !current || current.isEnded) return;
      const listed = rows.findIndex(({id}) => id === row.id);
      const result = analyticsResult(row, listed >= 0 ? listed : (index ?? 0));
      if (!result) return;
      const request = requestRef.current;
      if (request && request.live === live && request.filter === filter && listed >= 0) {
        current.resultSelected({
          requestId: request.id,
          activeFilter: filter,
          result,
          selectionMethod: method,
        });
      } else {
        // Picked from the empty box (your people) or the pane, not a query's results.
        current.end({
          activeFilter: filter,
          endReason: 'selected',
          outcome: outcomeForEntity(result.entityType),
        });
      }
    },
    [enabled, rows, live, filter]
  );

  /** Something stayed in the pane long enough to be looked at. */
  const paneViewed = useCallback(() => {
    paneViewedRef.current = true;
  }, []);

  return {selected, paneViewed};
};
